import html_default from "./template.default.html?raw";
const app_theme = import.meta.env.VITE_APP_THEME;
let html = "";
if ("default" == app_theme) {
  html = html_default;
}
import {
  inject,
  ref,
  watch,
  onMounted,
  reactive,
  type SetupContext,
} from "vue";

import { type IValidationErrors } from "../../abstractions/IValidationErrors.ts";
import TsmSpinner from "../TsmSpinner/index.ts";
import type { CartPatchDto } from "../../shared/CartPatchDto.ts";
import type { CartSubmitDto } from "../../shared/CartSubmitDto.ts";
import type { CartService } from "../../application/services/CartService.ts";
import type { CartSpecialPriceDto } from "../../shared/CartSpecialPriceDto.ts";
import type { CourseDto } from "../../shared/CourseDto.ts";
import type { CartDto } from "../../shared/CartDto.ts";
import type { UserService } from "../../application/services/UserService.ts";
import type { CustomerMeDto } from "../../shared/CustomerMeDto.ts";

interface Props {
  cart: CartDto;
  course: CourseDto;
  me: CustomerMeDto | null;
  validationErrors: IValidationErrors;
}

export default {
  template: html,
  components: { TsmSpinner },
  emits: {
    onChange: (value: CartPatchDto) => typeof value === "object",
    onSubmit: (value: CartSubmitDto) => typeof value === "object", 
  },
  props: {
    cart: {
      type: Object,
      required: true,
    },
    course: {
      type: Object,
      required: true,
    },
    me: {
      type: Object,
      required: false,
      default: null,
    },
    validationErrors: {
      type: Object,
      required: true,
    },
  },
  setup(props: Props, { emit }: SetupContext) {
    const initialized = ref(false);
    const patchLoading = ref(false);
    const customerPairDancerType = ref("L");
    const partnerPairDancerType = ref("F");
    const agbLink = ref("")
    const comment = ref<string>("");
    const splitInvoices = ref<boolean>(false);
    const customerSpecialPriceId = ref<number>(0);
    const partnerSpecialPriceId = ref<number>(0);
    const customerSpecialPrices = ref<CartSpecialPriceDto[]>([]);
    const partnerSpecialPrices = ref<CartSpecialPriceDto[]>([]);
    const reactiveCart = reactive<CartDto>({} as CartDto);

    const cartService: CartService | undefined = inject("cartService");
    const userService: UserService| undefined = inject("userService")

    const getError = function (key: string): string {
      let keyInApi = key.charAt(0).toUpperCase() + key.slice(1);
      if (props.validationErrors && keyInApi in props.validationErrors) {
        return props.validationErrors[keyInApi].join(", ");
      }
      return "";
    };

    const save = function () {
      let patch: CartPatchDto = {} as CartPatchDto

      if (props.course.courseType == 1) {
        patch.customerPairDancerType = "L";
        patch.partnerPairDancerType = "F";
        patch.splitInvoices = false;
      } else {
        patch.customerPairDancerType = customerPairDancerType.value;
        patch.partnerPairDancerType = partnerPairDancerType.value;
        patch.splitInvoices = splitInvoices.value;
      }
      patch.comment = comment.value;
      patchLoading.value = true;
      emit("onChange", patch);
      var inter = window.setInterval(function () {
        if (patchLoading.value == false) {
          window.clearInterval(inter);
          let subm: CartSubmitDto = {agbAccepted: true} as CartSubmitDto
          emit("onSubmit", subm);
        }
      }, 300);
    };

    const toLocaleDateString = function (dateStr: string) {
      const date = new Date(dateStr);
      return date.toLocaleDateString();
    };
    const nl2br = function (str: string | null) {
      if (null == str) {
        return "";
      }
      return str.replace(/\n/g, "<br>");
    };

    const updateSpecialPrice = async function () {
      // Damit Preisänderungen sofort sichtbar
      const patch: CartPatchDto = {} as CartPatchDto
      patch.customerSpecialPriceId = customerSpecialPriceId.value;
      patch.partnerSpecialPriceId = partnerSpecialPriceId.value;
      emit(
        "onChange",
        patch
      );
    };

    watch(customerPairDancerType, function (val) {
      // Partnertyp soll der inverse Wert sein
      partnerPairDancerType.value = "F" == val ? "L" : "F";
    });

    watch(
      () => props.cart,
      (newCart) => {
        patchLoading.value = false;
        Object.assign(reactiveCart, newCart);
      },
      { deep: true },
    );

    onMounted(async function () {
      // Neuer Warenkorb hat noch keine Rolle: Vorbelegung aus dem Kundenstamm (eingeloggt) oder Leader
      const known = (v: string | null | undefined) => v == "L" || v == "F";
      const fromMe = props.me?.pairDancerType;
      customerPairDancerType.value = known(props.cart.customerPairDancerType) ? props.cart.customerPairDancerType : known(fromMe) ? (fromMe as string) : "L";
      partnerPairDancerType.value = customerPairDancerType.value == "L" ? "F" : "L";
      comment.value = props.cart.comment ?? "";
      splitInvoices.value = props.cart.splitInvoices;
      partnerSpecialPriceId.value = props.cart.partnerSpecialPriceId;
      customerSpecialPriceId.value = props.cart.customerSpecialPriceId;
      initialized.value = true;
      if (cartService) {
        try {
          customerSpecialPrices.value =
            await cartService.getCustomerSpecialPrices();
          partnerSpecialPrices.value =
            await cartService.getPartnerSpecialPrices();
        } catch (err) {
          console.log(err);
        }
      }
      if (userService){
        const user = await userService.getUserInfo()
        agbLink.value = user?.agbLink ?? ""

      }
    });

    return {
      agbLink,
      customerPairDancerType,
      customerSpecialPriceId,
      customerSpecialPrices,
      partnerSpecialPriceId,
      partnerPairDancerType,
      partnerSpecialPrices,
      updateSpecialPrice,
      splitInvoices,
      comment,
      nl2br,
      initialized,
      save,
      toLocaleDateString,
      getError,
      errors: props.validationErrors,
      patchLoading,
      props
    };
  },
};
